/**
 * @file Dedicated heartbeat API client for isolated exam sessions.
 *
 * Uses a separate {@link Client} instance so that:
 * - POST requests are retried on network errors, 5xx, and 429 (the default
 *   client only retries idempotent methods).
 * - The global `processError` handler is suppressed — 401 responses from
 *   expired sessions are handled by the heartbeat loop itself, not the generic
 *   "session expired" toast.
 */
import moment from 'moment';
import * as api from '@codegrade/api';
import { RateLimitExceededException } from '@codegrade/api-models';
import { Maybe } from '@codegrade/maybe';
import { Client } from '@/client';
import { isNetworkError } from '@/utils/error';
const MAX_ATTEMPTS = 4;
/**
 * Compute the base retry delay: exactly the server-provided `retryAfter`
 * when rate limited, otherwise exponential backoff with 0-20% jitter.
 *
 * @param amountOfFailedAttempts How many attempts have already failed.
 * @param error The error from the most recent failed attempt.
 * @returns The delay to sleep before the next attempt.
 */
function getBaseRetryDelay(amountOfFailedAttempts, error) {
    if (error instanceof api.ApiResultError && error.error instanceof RateLimitExceededException) {
        return error.error.retryAfter;
    }
    return moment.duration(2 ** amountOfFailedAttempts * 500 * (1 + 0.2 * Math.random()));
}
const heartbeatClient = new Client({
    shouldRetry: {
        tag: 'custom',
        get: ({ amountOfFailedAttempts, error }) => {
            if (amountOfFailedAttempts >= MAX_ATTEMPTS) {
                return false;
            }
            if (error instanceof api.ApiResultError) {
                if (error.error instanceof RateLimitExceededException) {
                    return true;
                }
                const status = error.response.status;
                // 401 (session expired) and 403 (not in isolated session) are
                // terminal — a retry would never succeed. 5xx and 429 are
                // transient; a fresh mount right after a previous page sent a
                // heartbeat can still trip the backend rate limit.
                if (status === 401 || status === 403) {
                    return false;
                }
                return (status >= 500 && status < 600) || status === 429;
            }
            return isNetworkError(error);
        },
    },
    delayBetweenTries: {
        tag: 'custom',
        get: ({ amountOfFailedAttempts, error }) => {
            const delay = getBaseRetryDelay(amountOfFailedAttempts, error);
            // Before the final attempt, hold off at least one full rate-limit
            // window (with a small safety margin) so the retry outlasts any
            // burst captured by the backend's moving window — the final
            // attempt is the last thing standing between the student and
            // logout.
            if (amountOfFailedAttempts >= MAX_ATTEMPTS - 1) {
                const minimum = moment.duration(16, 's');
                if (delay.asMilliseconds() < minimum.asMilliseconds()) {
                    return minimum;
                }
            }
            return delay;
        },
    },
    processError: Maybe.empty(),
});
/**
 * Send a single heartbeat to the backend.
 *
 * Retries on network errors, 5xx responses, and 429 rate-limit responses (up
 * to {@link MAX_ATTEMPTS} total attempts). When the backend signals a rate
 * limit with a {@link RateLimitExceededException}, the next attempt waits
 * exactly the server-provided `retryAfter` so interleaved tabs do not spin
 * against the window. Does NOT retry on 401 (session expired) or 403 (not in
 * isolated session) — these are terminal.
 *
 * @returns The session's ``expires_at`` after the refresh.
 * @throws When the heartbeat fails after all retry attempts.
 */
export async function sendHeartbeat() {
    const res = await heartbeatClient.p.restriction.heartbeat();
    return res.cgResult.expiresAt;
}
